import { QUERY_STATUS } from 'pages/CleanRooms/LiveCleanRoom/constant';
import { activeOrgSelector } from 'redux/reducer/AuthenticationReducer';
import { axiosInstance } from 'utils/http';
import { liveCleanroom } from 'utils/urlConstants';
import { showAlert } from 'redux/actions/AlertActions';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import { useQuery } from 'react-query';


const MAX_REFETCH_INTERVAL = 8000; // Maximum polling interval of 8 seconds
const INITIAL_REFETCH_INTERVAL = 1000; // Start with 1 second

export const usePollingLcrWorksheetQueryStatus = (lcrSessionId, worksheetId, queryId) => {
  const dispatch = useDispatch();
  const { ID: orgId } = useSelector(activeOrgSelector);
  const lastNotifiedStatus = useRef(null);
  const lastQueryId = useRef(null);
  const isInitialLoad = useRef(true);
  const pollCount = useRef(0);

  // Reset status tracker when queryId changes (a new query started)
  useEffect(() => {
    if (queryId && queryId !== lastQueryId.current) {
      lastNotifiedStatus.current = null;
      lastQueryId.current = queryId;
      isInitialLoad.current = true;
      pollCount.current = 0;
    }

    // Clean up on unmounting or when queryId changes
    return () => {
      if (queryId === lastQueryId.current) {
        pollCount.current = 0;
      }
    };
  }, [queryId]);

  return useQuery(
    ['lcrWorksheetQueryStatusPolling', lcrSessionId, worksheetId, queryId],
    () => {
      // Increment poll count here, before the request
      pollCount.current += 1;

      return axiosInstance.get(
        liveCleanroom(orgId).monitorWorksheetQuery(
          lcrSessionId,
          worksheetId,
          queryId,
        ),
      );
    },
    {
      enabled: !!worksheetId && !!lcrSessionId && !!queryId,
      refetchInterval: (data) => {
        const status = data?.status;

        // Stop polling when a query is completed or failed
        if (status === QUERY_STATUS.COMPLETED || status === QUERY_STATUS.FAILED) {
          return false;
        }

        // Use exponential backoff for a polling interval, capped at the maximum value
        return Math.min(
          INITIAL_REFETCH_INTERVAL * 2 ** (pollCount.current - 1),
          MAX_REFETCH_INTERVAL,
        );
      },
      refetchIntervalInBackground: false,
      select: (response) => response.data,
      onSuccess: (data) => {
        const status = data?.status;

        // Handle initial load - just set the status without showing notifications
        if (isInitialLoad.current) {
          lastNotifiedStatus.current = status;
          isInitialLoad.current = false;
          return;
        }

        // Only show toast if the status has changed from the last notification
        if (status && status !== lastNotifiedStatus.current) {
          lastNotifiedStatus.current = status;

          if (status === QUERY_STATUS.COMPLETED) {
            dispatch(
              showAlert({
                message: 'Query completed successfully',
                type: 'success',
              }),
            );
          }
          else if (status === QUERY_STATUS.FAILED) {
            dispatch(
              showAlert({
                message: 'Query failed',
                type: 'error',
              }),
            );
          }
        }
      },
      onError: (error) => {
        // Don't show error for aborted requests (happens during unmounting)
        if (!error.name || error.name !== 'AbortError') {
          dispatch(
            showAlert({
              message:
                error.response?.data?.message || 'Error polling query status',
              type: 'error',
            }),
          );
        }
      },
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors, but retry on network/5xx errors
        if (error.response?.status >= 400 && error.response?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
    },
  );
};
